import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/**
 * Drag-to-resize + fullscreen state for a center-anchored modal, persisted to
 * localStorage so it reopens the way the user left it.
 *
 * The modal box is centered via `translate(-50%, -50%)`, so when an edge is
 * dragged the box grows symmetrically from the center — the dragged edge moves
 * by HALF the dimension change. We therefore double the mouse delta so the
 * handle tracks the cursor 1:1.
 */

export type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface ResizableModalState {
  width: number;
  height: number;
  isFullscreen: boolean;
}

interface UseResizableModalOptions {
  /** kebab-case, versioned localStorage key, e.g. 'content-creation-wizard-size-v1' */
  storageKey: string;
  defaultWidth: number;
  defaultHeight: number;
  minWidth?: number;
  minHeight?: number;
  /** px margin kept from the viewport edges (max size). */
  viewportMargin?: number;
  /**
   * Open maximized when nothing is persisted yet. Bump the caller's storageKey
   * version to push a new default onto users who already have a stored choice.
   */
  defaultFullscreen?: boolean;
}

const clamp = (value: number, lo: number, hi: number) =>
  Math.min(Math.max(value, lo), Math.max(lo, hi));

function readPersisted(key: string): Partial<ResizableModalState> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<ResizableModalState>) : null;
  } catch {
    return null;
  }
}

export function useResizableModal({
  storageKey,
  defaultWidth,
  defaultHeight,
  minWidth = 420,
  minHeight = 320,
  viewportMargin = 16,
  defaultFullscreen = false,
}: UseResizableModalOptions) {
  const [state, setState] = useState<ResizableModalState>(() => {
    const persisted = readPersisted(storageKey);
    return {
      width: persisted?.width ?? defaultWidth,
      height: persisted?.height ?? defaultHeight,
      isFullscreen: persisted?.isFullscreen ?? defaultFullscreen,
    };
  });
  const [isResizing, setIsResizing] = useState(false);

  // Mirror the latest size into a ref so the (memoized) resize handler can read
  // the current dimensions at mousedown without being recreated on every change.
  const sizeRef = useRef({ width: state.width, height: state.height });
  useEffect(() => {
    sizeRef.current = { width: state.width, height: state.height };
  }, [state.width, state.height]);

  // Persist on change (best-effort; ignore quota / disabled storage).
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      /* no-op */
    }
  }, [storageKey, state]);

  const toggleFullscreen = useCallback(() => {
    setState((s) => ({ ...s, isFullscreen: !s.isFullscreen }));
  }, []);

  const startResize = useCallback(
    (dir: ResizeDir) => (event: ReactMouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsResizing(true);

      const startX = event.clientX;
      const startY = event.clientY;
      const startW = sizeRef.current.width;
      const startH = sizeRef.current.height;

      const maxW = window.innerWidth - viewportMargin * 2;
      const maxH = window.innerHeight - viewportMargin * 2;

      const onMove = (e: globalThis.MouseEvent) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        let w = startW;
        let h = startH;
        if (dir.includes('e')) w = startW + dx * 2;
        if (dir.includes('w')) w = startW - dx * 2;
        if (dir.includes('s')) h = startH + dy * 2;
        if (dir.includes('n')) h = startH - dy * 2;
        setState((s) => ({
          ...s,
          width: clamp(w, minWidth, maxW),
          height: clamp(h, minHeight, maxH),
        }));
      };
      const onUp = () => {
        setIsResizing(false);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [minWidth, minHeight, viewportMargin],
  );

  const contentStyle: CSSProperties = state.isFullscreen
    ? {
        width: `calc(100vw - ${viewportMargin * 2}px)`,
        height: `calc(100vh - ${viewportMargin * 2}px)`,
        maxWidth: 'none',
        maxHeight: 'none',
      }
    : { width: state.width, height: state.height, maxWidth: 'none', maxHeight: 'none' };

  return {
    width: state.width,
    height: state.height,
    isFullscreen: state.isFullscreen,
    isResizing,
    toggleFullscreen,
    startResize,
    contentStyle,
  };
}
